/**
 * Taking a table away as a file.
 *
 * A job, and **not one of the jobs `/api/jobs/` knows about**. Exports predate
 * that system and keep their own: their own endpoint, their own state enum, and
 * their own polling. Folding them into the shared job list would mean teaching
 * it a second shape for no gain -- nothing else needs to see an export, because
 * the only thing anybody does with one is download it from the dialog that
 * started it.
 *
 * **CSV is the only format.** The JSON, XML and Excel exporters were premium and
 * enterprise, and went with those trees; the endpoint's own discriminator now
 * has one entry. So there is no format to pick, and a picker with one option in
 * it would be a control that cannot be used.
 */
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { CsvColumnSeparator, ExportCharset, ExportJob } from '@/api/types'

/** What the dialog asks for, which is a subset of what the endpoint takes. */
export interface ExportRequest {
  /** The view to export, or null for the whole table. */
  viewId: number | null
  charset: ExportCharset
  separator: CsvColumnSeparator
  includeHeader: boolean
}

/**
 * Whether the file is still being written.
 *
 * `expired` counts as finished: the job ran, the file was made, and it has since
 * been cleaned up -- which is a thing to say rather than a thing to wait for.
 */
export function isExportRunning(job: ExportJob | undefined): boolean {
  return job?.state === 'pending' || job?.state === 'exporting'
}

export function hasExportFailed(job: ExportJob | undefined): boolean {
  return job?.state === 'failed' || job?.state === 'cancelled'
}

/**
 * One export job, polled while it runs.
 *
 * The interval is derived from the job rather than held in a timer, the same
 * way the shared job list derives its own: a finished job stops the polling by
 * being finished, with nothing to remember to cancel.
 */
export function exportJobQuery(jobId: number) {
  return queryOptions({
    queryKey: qk.exportJob(jobId),
    queryFn: (): Promise<ExportJob> =>
      unwrap(
        getApi().GET('/api/database/export/{job_id}/', {
          params: { path: { job_id: jobId } },
        }),
      ),
    refetchInterval: (query) => (isExportRunning(query.state.data) ? 1000 : false),
  })
}

export function useExportJob(jobId: number | null) {
  return useQuery({ ...exportJobQuery(jobId ?? 0), enabled: jobId !== null })
}

export function useStartExport(tableId: number) {
  return useMutation({
    mutationFn: ({
      viewId,
      charset,
      separator,
      includeHeader,
    }: ExportRequest): Promise<ExportJob> =>
      unwrap(
        getApi().POST('/api/database/export/table/{table_id}/', {
          params: { path: { table_id: tableId } },
          body: {
            exporter_type: 'csv',
            // Null rather than absent: the endpoint reads it as "the whole
            // table", and leaving the key out would be the same thing said less
            // clearly.
            view_id: viewId,
            export_charset: charset,
            csv_column_separator: separator,
            csv_include_header: includeHeader,
            // Not offered, and sent because the endpoint declares them
            // required. Both are its own documented defaults: a CSV of a table
            // without the column that names each row is a CSV of anonymous
            // rows, and without the row id there is no way back to the row.
            include_row_id: true,
            include_primary_field: true,
          },
        }),
      ),
  })
}
